import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Calculator, TrendingUp, ShieldCheck, ChevronRight, Menu, X, FileText, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageWrapper from '../components/landing/PageWrapper';
import SectionLoader from '../components/landing/SectionLoader';
import MockupApp from '../components/landing/MockupApp';
import logoRmaxJpeg from '../assets/logo-rmax.jpeg';
import logoRmaxWebp from '../assets/logo-rmax.webp';
import { useIsPlayStore } from '../utils/playstore';
import { HideOnPlayStore } from '../components/HideOnPlayStore';
import { PlayStoreBadge } from '../components/common/PlayStoreBadge';
import { safeStorage } from '../utils/safeStorage';

// Dynamic / Lazy loaded components for code-splitting
import PricingSection from '../components/landing/PricingSection';
import ProfitSimulator from '../components/landing/ProfitSimulator';
import HowItWorks from '../components/landing/HowItWorks';
import NicheGatewaySection from '../components/landing/NicheGatewaySection';
import PaywallModal from '../components/PaywallModal';
const Guarantee = lazy(() => import('../components/landing/Guarantee'));
const FAQ = lazy(() => import('../components/landing/FAQ'));
const Footer = lazy(() => import('../components/landing/Footer'));
const WhatsAppSupport = lazy(() => import('../components/WhatsAppSupport').then(m => ({ default: m.WhatsAppSupport })));
const LeadModal = lazy(() => import('../components/landing/LeadModal'));
const AboutCreatorSection = lazy(() => import('../components/landing/AboutCreatorSection'));

interface LandingPageProps {
  scrollTo?: string;
}

export default function LandingPage({ scrollTo }: LandingPageProps = {}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [paywallPlan, setPaywallPlan] = useState<
    'monthly' | 'annual' | 'specialist' | 'specialist_annual' | 'enterprise' | 'enterprise_annual' | 'enterprise_anual_pix' | 'empresa' | 'empresa_anual' | 'empresa_annual'
  >('annual');
  const navigate = useNavigate();
  const { status } = useAuth();
  const isPlayStore = useIsPlayStore();

  // Auto-redirecionamento instantâneo: se o usuário já estiver conectado ou se houver parâmetro de visualização (?view=), pula a Landing Page e vai direto para o App
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const viewParam = params.get('view');
      if (viewParam) {
        navigate(`/app${window.location.search}`, { replace: true });
        return;
      }
    } catch {}

    if (status === 'authenticated') {
      navigate('/app', { replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    if (
      scrollTo === 'precos' ||
      window.location.pathname === '/planos' ||
      window.location.pathname === '/precos' ||
      window.location.hash === '#precos'
    ) {
      const timer = setTimeout(() => {
        const element = document.getElementById('precos');
        if (element) {
          const offset = 80;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const offsetPosition = elementRect - bodyRect - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [scrollTo]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isAuthCallback = typeof window !== 'undefined' && (
    window.location.hash.includes('access_token=') ||
    window.location.hash.includes('type=magiclink') ||
    window.location.search.includes('code=')
  );

  // Splash de transição quando o usuário clica no Magic Link
  if (status === 'loading' && isAuthCallback) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center font-sans">
        <div className="w-14 h-14 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-4" />
        <h2 className="text-lg font-black text-slate-900 mb-1">Entrando no seu aplicativo...</h2>
        <p className="text-slate-500 text-xs">Validando seu acesso seguro. Você será redirecionado em instantes.</p>
      </div>
    );
  }

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/')) {
      setIsMenuOpen(false);
      return;
    }
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      const offset = 80; // Header height
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  const handleOpenModal = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setIsModalOpen(true);
  };

  const handleLeadSuccess = (_email: string, _coupon: string) => {
    const pendingPlan = safeStorage.getItem('rmax_pending_plan');
    const refCoupon = safeStorage.getItem('rmax_referred_coupon');

    if (refCoupon) {
      if (refCoupon.includes('EMPRESA')) {
        navigate('/selecionar-perfil?tab=empresa');
        return;
      }
      if (refCoupon.includes('PRO')) {
        navigate('/selecionar-perfil?tab=pro_gratis');
        return;
      }
      navigate('/selecionar-perfil?tab=especialista');
      return;
    }

    if (pendingPlan === 'gratis') {
      navigate('/app');
    } else if (pendingPlan === 'pro') {
      navigate('/selecionar-perfil?tab=pro_gratis');
    } else if (pendingPlan === 'especialista' || pendingPlan === 'premium' || pendingPlan === 'specialist') {
      navigate('/selecionar-perfil?tab=especialista');
    } else if (pendingPlan === 'empresa' || pendingPlan === 'enterprise') {
      navigate('/selecionar-perfil?tab=empresa');
    } else {
      navigate('/app');
    }
  };

  const navLinks = [
    { name: 'Ramos Atendidos', href: '#nichos' },
    { name: 'Como Funciona', href: '#como-funciona' },
    { name: 'Benefícios', href: '#beneficios' },
    ...(!isPlayStore ? [{ name: 'Preços', href: '#precos' }] : []),
    { name: 'Calculadora Grátis', href: '/calculadora-preco-venda' },
    { name: 'Garantia', href: '#garantia' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Parceiros', href: '/influenciador' },
  ];

  return (
    <PageWrapper className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-emerald-500/20 scroll-smooth">
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-100 focus:px-6 focus:py-3 focus:bg-emerald-600 focus:text-white focus:rounded-full focus:font-bold focus:shadow-xl outline-none ring-2 ring-emerald-400 min-h-12 flex items-center justify-center"
      >
        Pular para o conteúdo principal
      </a>

      {/* Mobile Sticky CTA */}
      {!isMenuOpen && (
        <div className="md:hidden fixed bottom-6 left-0 right-0 z-60 px-6 pointer-events-none">
          <button 
            onClick={() => navigate('/selecionar-perfil')}
            aria-label="Escolher ramo de atuação e começar"
            className="w-full pointer-events-auto py-4 px-6 min-h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold rounded-2xl shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 active:scale-95 transition-all cursor-pointer"
          >
            Escolher meu Ramo e Começar
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Navbar Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-xs py-2' : 'bg-transparent py-4'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <Link 
            to="/" 
            className="flex items-center gap-3 min-h-12 min-w-12 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-lg p-1"
            aria-label="RESOLV MAX - Página Inicial"
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            {/* Optimized LCP Image Delivery: WebP with Fallback + explicit width/height + fetchpriority high */}
            <picture className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
              <source srcSet={logoRmaxWebp} type="image/webp" />
              <img 
                src={logoRmaxJpeg} 
                alt="RESOLV MAX Logo" 
                width="48" 
                height="48" 
                loading="eager" 
                fetchPriority="high"
                decoding="async"
                className="w-10 h-10 md:w-12 md:h-12 object-contain rounded-xl" 
              />
            </picture>
            <span className="text-2xl font-black tracking-tighter text-slate-950">RESOLV MAX</span>
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-2" aria-label="Navegação Principal">
            {navLinks.map((link) => (
              link.href.startsWith('/') ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors px-4 py-3 min-h-12 flex items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {link.name}
                </Link>
              ) : (
                <a 
                  key={link.name} 
                  href={link.href} 
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors px-4 py-3 min-h-12 flex items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {link.name}
                </a>
              )
            ))}
            {status === 'authenticated' ? (
              <button 
                onClick={() => navigate('/app')}
                className="text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all rounded-full px-6 py-3 min-h-12 min-w-12 flex items-center justify-center shadow-md shadow-emerald-600/20 ml-4 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
              >
                Meu Painel (App)
              </button>
            ) : (
              <div className="flex items-center gap-2 ml-4">
                <button 
                  onClick={() => navigate('/auth')}
                  className="text-sm font-bold text-slate-700 hover:text-slate-950 bg-white hover:bg-slate-100 border border-slate-300 transition-all rounded-full px-5 py-2.5 min-h-12 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer"
                >
                  Já tenho conta
                </button>
                <button 
                  onClick={() => navigate('/selecionar-perfil')}
                  className="text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all rounded-full px-5 py-2.5 min-h-12 flex items-center justify-center shadow-md shadow-emerald-600/20 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                >
                  Começar Agora
                </button>
              </div>
            )}
          </nav>

          {/* Mobile Direct Access Button + Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            {status === 'authenticated' ? (
              <button 
                type="button"
                onClick={() => navigate('/app')}
                className="text-xs font-black text-white bg-emerald-600 hover:bg-emerald-700 transition-all rounded-full px-4 py-2 min-h-10 flex items-center justify-center shadow-sm shadow-emerald-600/20 active:scale-95 cursor-pointer"
              >
                Meu Painel
              </button>
            ) : (
              <button 
                type="button"
                onClick={() => navigate('/auth')}
                className="text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 transition-all rounded-full px-3 py-2 min-h-10 flex items-center justify-center active:scale-95 cursor-pointer"
              >
                Entrar
              </button>
            )}
            <button 
              className="p-2.5 min-h-11 min-w-11 text-slate-700 hover:text-slate-950 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-lg cursor-pointer"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Fechar menu de navegação" : "Abrir menu de navegação"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 p-6 flex flex-col gap-2 animate-in slide-in-from-top duration-300 shadow-xl">
            {navLinks.map((link) => (
              link.href.startsWith('/') ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-lg font-semibold text-slate-700 hover:text-emerald-700 py-3.5 px-4 min-h-12 flex items-center border-b border-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ) : (
                <a 
                  key={link.name} 
                  href={link.href} 
                  className="text-lg font-semibold text-slate-700 hover:text-emerald-700 py-3.5 px-4 min-h-12 flex items-center border-b border-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-lg"
                  onClick={(e) => scrollToSection(e, link.href)}
                >
                  {link.name}
                </a>
              )
            ))}
            {status === 'authenticated' ? (
              <button
                onClick={() => { setIsMenuOpen(false); navigate('/app'); }}
                className="mt-4 text-center text-base font-bold text-white bg-emerald-600 hover:bg-emerald-700 py-3.5 min-h-12 flex items-center justify-center rounded-xl cursor-pointer shadow-md shadow-emerald-600/20"
              >
                Meu Painel (App)
              </button>
            ) : (
              <div className="mt-4 flex flex-col gap-2">
                <button
                  onClick={() => { setIsMenuOpen(false); navigate('/selecionar-perfil'); }}
                  className="w-full text-center text-base font-bold text-white bg-emerald-600 hover:bg-emerald-700 py-3.5 min-h-12 flex items-center justify-center rounded-xl cursor-pointer shadow-md shadow-emerald-600/20"
                >
                  Começar Agora (Escolher Ramo)
                </button>
                <button
                  onClick={() => { setIsMenuOpen(false); navigate('/auth'); }}
                  className="w-full text-center text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 py-3 min-h-11 flex items-center justify-center rounded-xl cursor-pointer"
                >
                  Já tenho conta (Entrar)
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <main id="main-content" className="pt-20 overflow-x-hidden">
        {/* 1. HERO SECTION */}
        <section id="hero" className="relative container mx-auto px-6 pt-6 md:pt-20 pb-20 md:pb-32 overflow-hidden" aria-labelledby="hero-heading">
          {/* Background Glow */}
          <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-200 h-200 bg-emerald-500/10 blur-[120px] rounded-full pointer-events-none" aria-hidden="true"></div>
          
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center relative z-10">
            <div className="max-w-2xl text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs md:text-sm font-bold mb-6 uppercase tracking-wider">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-600 animate-pulse" aria-hidden="true"></span>
                RESOLV MAX: Precificação Inteligente
              </div>
              <h1 id="hero-heading" className="text-3xl md:text-6xl lg:text-7xl font-extrabold leading-[1.15] md:leading-[1.1] tracking-tight mb-6 text-slate-950">
                Pare de Perder Dinheiro. <span className="text-transparent bg-clip-text bg-linear-to-r from-emerald-600 to-teal-600">Domine seu Lucro</span> de Vez.
              </h1>
              <p className="text-base md:text-xl text-slate-600 mb-8 leading-relaxed font-normal px-2 md:px-0">
                O R-MAX adapta ferramentas de gestão para o seu ramo: calcule custos e Valor Hora, emita orçamentos em PDF com margem protegida, controle ordens de serviço ou opere frente de caixa no balcão.
              </p>
              <div className="flex flex-col sm:flex-row items-center md:items-start gap-4 px-4 md:px-0">
                <button 
                  onClick={() => navigate('/selecionar-perfil')}
                  className="w-full sm:w-auto px-8 py-4 min-h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-base md:text-lg transition-all transform hover:scale-105 shadow-md shadow-emerald-600/20 focus:outline-none focus:ring-2 focus:ring-emerald-500 flex items-center justify-center gap-2 cursor-pointer"
                >
                  Escolher meu Ramo e Começar
                  <ChevronRight className="w-5 h-5" aria-hidden="true" />
                </button>
                <PlayStoreBadge variant="badge-light" utmCampaign="hero_landing" />
              </div>
              <p className="mt-4 text-xs md:text-sm text-slate-600 flex items-center justify-center md:justify-start gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" aria-hidden="true" />
                Plano Grátis Permanente: 5 orçamentos todo mês. Sem cartão.
              </p>
            </div>
            
            <div className="relative flex justify-center items-center mt-4 md:mt-0" aria-hidden="true">
              {/* CSS-based 3D Smartphone Mockup */}
              <div className="relative w-full max-w-60 md:max-w-[320px] aspect-[1/2.1] rounded-4xl md:rounded-5xl border-[6px] md:border-10 border-slate-800 bg-slate-900 shadow-2xl shadow-slate-900/15 overflow-hidden transform md:rotate-[-5deg] hover:rotate-0 transition-transform duration-700 ease-out group">
                {/* Notch */}
                <div className="absolute top-0 inset-x-0 h-7 bg-slate-800 rounded-b-3xl w-1/2 mx-auto z-20"></div>
                
                {/* Screen Content - Recreating the App UI */}
                <MockupApp />
                
                {/* Glare effect */}
                <div className="absolute inset-0 bg-linear-to-tr from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
              </div>
            </div>
          </div>
        </section>

        {/* 1.2 NICHE GATEWAY SECTION */}
        <NicheGatewaySection />

        {/* 1.5 HOW IT WORKS */}
        <section id="como-funciona" className="scroll-mt-20">
          <HowItWorks />
        </section>

        {/* 2. SOCIAL PROOF */}
        <section className="border-y border-slate-200 bg-white py-10 overflow-hidden" aria-labelledby="social-proof-heading">
          <div className="container mx-auto px-6">
            <h2 id="social-proof-heading" className="text-center text-xs md:text-sm font-bold text-slate-500 mb-8 uppercase tracking-widest">
              Ajudando mais de 100.000 empreendedores a dominarem suas margens de lucro.
            </h2>
            
            {/* Marquee Container */}
            <div className="relative flex overflow-x-hidden group">
              {/* Gradient Masks */}
              <div className="absolute top-0 bottom-0 left-0 w-24 bg-linear-to-r from-white to-transparent z-10" aria-hidden="true"></div>
              <div className="absolute top-0 bottom-0 right-0 w-24 bg-linear-to-l from-white to-transparent z-10" aria-hidden="true"></div>
              
              {/* Scrolling Content */}
              <div className="flex animate-marquee whitespace-nowrap items-center gap-12 text-slate-700 font-bold opacity-90 hover:opacity-100 transition-opacity duration-500">
                <span className="text-xl font-bold tracking-tight">Confeitaria & Gastronomia</span>
                <span className="text-xl font-bold tracking-tight">Estética & Beleza</span>
                <span className="text-xl font-bold tracking-tight">Obras & Reparos</span>
                <span className="text-xl font-bold tracking-tight">Serviços Digitais & TI</span>
                <span className="text-xl font-bold tracking-tight">Eventos & Entretenimento</span>
                <span className="text-xl font-bold tracking-tight">Manutenção & Limpeza</span>
                <span className="text-xl font-bold tracking-tight">Mercado Pet</span>
                <span className="text-xl font-bold tracking-tight">Educação & Bem-Estar</span>
                <span className="text-xl font-bold tracking-tight">Varejo & Revenda</span>
                <span className="text-xl font-bold tracking-tight">Logística & Transporte</span>
                
                {/* Seamless loop duplication */}
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Confeitaria & Gastronomia</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Estética & Beleza</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Obras & Reparos</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Serviços Digitais & TI</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Eventos & Entretenimento</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Manutenção & Limpeza</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Mercado Pet</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Educação & Bem-Estar</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Varejo & Revenda</span>
                <span className="text-xl font-bold tracking-tight" aria-hidden="true">Logística & Transporte</span>
              </div>
            </div>
          </div>
        </section>

        {/* 3. BENEFITS GRID */}
        <section id="beneficios" className="container mx-auto px-6 py-24 md:py-32 scroll-mt-20" aria-labelledby="benefits-heading">
          <div className="text-center mb-16">
            <h2 id="benefits-heading" className="text-3xl md:text-5xl font-extrabold text-slate-950 mb-4">
              Por Que Escolher a Inteligência <span className="text-emerald-600">RESOLV MAX</span>
            </h2>
            <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto">
              Recursos desenhados especificamente para transformar a lucratividade do seu negócio.
            </p>
          </div>

          <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 overflow-x-auto md:overflow-x-visible pb-8 md:pb-0 snap-x snap-mandatory scrollbar-hide">
            {/* Card 1 */}
            <div className="min-w-[85%] md:min-w-0 p-7 rounded-3xl bg-white border border-slate-200 hover:border-emerald-500/50 shadow-sm hover:shadow-md transition-all group snap-center flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-100/70 transition-all" aria-hidden="true">
                  <Calculator className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3">Você no Controle</h3>
                <p className="text-slate-600 leading-relaxed font-normal text-sm">
                  Chega de depender de terceiros para entender seus números. Com o RESOLV MAX, você tem a ferramenta certa para gerir seu negócio com total autonomia e segurança.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="min-w-[85%] md:min-w-0 p-7 rounded-3xl bg-white border border-slate-200 hover:border-emerald-500/50 shadow-sm hover:shadow-md transition-all group snap-center flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-100/70 transition-all" aria-hidden="true">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3">Simulador de Preços</h3>
                <p className="text-slate-600 leading-relaxed font-normal text-sm">
                  Mude o preço no aplicativo e veja na hora quanto dinheiro limpo vai sobrar no seu bolso em cada venda ou serviço. Sem contas difíceis.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="min-w-[85%] md:min-w-0 p-7 rounded-3xl bg-white border border-slate-200 hover:border-emerald-500/50 shadow-sm hover:shadow-md transition-all group snap-center flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-100/70 transition-all" aria-hidden="true">
                  <FileText className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3">Orçamentos Profissionais</h3>
                <p className="text-slate-600 leading-relaxed font-normal text-sm">
                  Gere orçamentos em PDF com visual profissional e envie direto para o seu cliente em minutos. Tenha tudo salvo e sempre à mão no celular.
                </p>
              </div>
            </div>

            {/* Card 4 - Novo: Minha Equipe & Lucro Blindado */}
            <div className="min-w-[85%] md:min-w-0 p-7 rounded-3xl bg-white border-2 border-emerald-500/40 hover:border-emerald-500 transition-all group snap-center relative flex flex-col justify-between shadow-sm hover:shadow-md">
              <div className="absolute -top-3 right-6 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-0.5 rounded-full shadow-sm">
                EXCLUSIVO PREMIUM
              </div>
              <div>
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6 group-hover:scale-105 transition-all text-emerald-600" aria-hidden="true">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3">Equipe com Lucro Protegido</h3>
                <p className="text-slate-600 leading-relaxed font-normal text-sm">
                  Coloque até 10 colaboradores para vender e atender. Com a <strong>trava de segredo comercial</strong>, seus funcionários emitem orçamentos sem ver seus custos reais nem sua margem de lucro.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3.5 PROFIT SIMULATOR */}
        <ProfitSimulator />

        {/* 4. PRICING TABLE */}
        <HideOnPlayStore>
          <PricingSection 
            onOpenModal={handleOpenModal} 
            onSelectPlan={(planKey) => {
              if (status === 'authenticated') {
                setPaywallPlan(planKey);
                setIsPaywallOpen(true);
              } else {
                setIsModalOpen(true);
              }
            }}
          />
        </HideOnPlayStore>

        {/* 5. GUARANTEE */}
        <section id="garantia" className="container mx-auto px-6 scroll-mt-20" aria-label="Garantia RESOLV MAX">
          <Suspense fallback={<SectionLoader />}>
            <Guarantee />
          </Suspense>
        </section>

        {/* 6. FAQ */}
        <div id="faq" className="scroll-mt-20">
          <Suspense fallback={<SectionLoader />}>
            <FAQ />
          </Suspense>
        </div>

        {/* 7. ABOUT CREATOR */}
        <Suspense fallback={<SectionLoader />}>
          <AboutCreatorSection />
        </Suspense>
      </main>

      {/* 8. FOOTER & MODALS */}
      <Suspense fallback={null}>
        <Footer />
        <WhatsAppSupport />
        <LeadModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          onSuccess={handleLeadSuccess}
        />
        <PaywallModal 
          isOpen={isPaywallOpen}
          onClose={() => setIsPaywallOpen(false)}
          initialPlan={paywallPlan}
        />
      </Suspense>
    </PageWrapper>
  );
}

